import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import type { GameResult } from "../../../hooks/useGameState";

interface DailyWordProps {
  puzzle: {
    answer: string;
    allowed_guesses: string[];
  };
  gameState?: GameResult;
  onComplete: (result: GameResult) => void;
}

type LetterState = "correct" | "present" | "absent" | "empty";

interface Letter {
  letter: string;
  state: LetterState;
}

const MAX_TRIES = 6;
const WORD_LENGTH = 5;

export default function DailyWord({ puzzle, gameState, onComplete }: DailyWordProps) {
  const answer = puzzle.answer.toUpperCase();

  // State
  const [guesses, setGuesses] = useState<string[]>(gameState?.guesses || []);
  const [evaluations, setEvaluations] = useState<Letter[][]>([]); // << CORRECT FIX
  const [currentGuess, setCurrentGuess] = useState("");
  const [isComplete, setIsComplete] = useState(
    gameState?.status === "won" || gameState?.status === "lost"
  );
  const [shakeRow, setShakeRow] = useState(false);

  /** -------------------------------
   * WORD VALIDATION + EVALUATION
   * Correct Wordle logic, now optimized
   ---------------------------------*/
  const evaluateGuess = useCallback((guess: string): Letter[] => {
    const result: Letter[] = guess.split("").map((ch) => ({ letter: ch, state: "absent" }));
    const counts: Record<string, number> = {};

    // Build frequency map of answer
    for (const ch of answer) counts[ch] = (counts[ch] ?? 0) + 1;

    // FIRST PASS: mark correct
    for (let i = 0; i < WORD_LENGTH; i++) {
      if (guess[i] === answer[i]) {
        result[i].state = "correct";
        counts[guess[i]]--;
      }
    }

    // SECOND PASS: mark present if leftover exists
    for (let i = 0; i < WORD_LENGTH; i++) {
      if (result[i].state === "correct") continue;
      if (counts[guess[i]] > 0) {
        result[i].state = "present";
        counts[guess[i]]--;
      }
    }

    return result;
  }, [answer]);

  /** -------------------------------
   * KEYBOARD STATE (CORRECT PRIORITY)
   ---------------------------------*/
  const getKeyState = (letter: string): LetterState => {
    let state: LetterState = "empty";

    evaluations.forEach((row) =>
      row.forEach((tile) => {
        if (tile.letter !== letter) return;

        if (tile.state === "correct") state = "correct";
        else if (tile.state === "present" && state !== "correct") state = "present";
        else if (tile.state === "absent" && state === "empty") state = "absent";
      })
    );

    return state;
  };

  /** -------------------------------
   * KEY / INPUT HANDLER
   ---------------------------------*/
  const handleKey = useCallback(
    (key: string) => {
      if (isComplete) return;

      // Backspace
      if (key === "Backspace") {
        if (currentGuess.length > 0) setCurrentGuess((g) => g.slice(0, -1));
        return;
      }

      // Enter Submit
      if (key === "Enter") {
        if (currentGuess.length !== WORD_LENGTH) return triggerShake();
        const guess = currentGuess.toUpperCase();

        // Check if word is in allowed guesses or is the answer
        const allowedWords = new Set([
          ...puzzle.allowed_guesses.map((w) => w.toUpperCase()),
          answer,
        ]);
        if (!allowedWords.has(guess)) return triggerShake();

        const result = evaluateGuess(guess);

        // Push into state once (fixed)
        const newGuesses = [...guesses, guess];
        setGuesses(newGuesses);
        setEvaluations((prev) => [...prev, result]);
        setCurrentGuess("");

        // WIN
        if (guess === answer) {
          setIsComplete(true);
          triggerConfetti();
          return onComplete({ status: "won", guesses: newGuesses, tries: newGuesses.length });
        }

        // LOSE
        if (newGuesses.length >= MAX_TRIES) {
          setIsComplete(true);
          return onComplete({ status: "lost", guesses: newGuesses, tries: MAX_TRIES });
        }
        return;
      }

      // Letter key
      if (/^[A-Za-z]$/.test(key) && currentGuess.length < WORD_LENGTH) {
        setCurrentGuess((g) => g + key.toUpperCase());
        return;
      }
    },
    [currentGuess, guesses, isComplete, evaluateGuess, answer, puzzle.allowed_guesses, onComplete]
  );

  // Key listener binding - only capture when not typing in other inputs
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input, textarea, or contenteditable element
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.getAttribute("contenteditable") === "true")
      ) {
        return; // Let the input handle it - don't interfere
      }
      
      // Only handle keys relevant to Daily Word game
      if (e.key === "Enter" || e.key === "Backspace" || /^[A-Za-z]$/.test(e.key)) {
        e.preventDefault(); // Prevent default scrolling/focus behavior
        handleKey(e.key);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleKey]);

  // Helper
  const triggerShake = () => {
    setShakeRow(true);
    setTimeout(() => setShakeRow(false), 350);
  };

  /** -------------------------------
   *  UI GRID RENDERING
   ---------------------------------*/
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Daily Word</h2>
          {!isComplete && (
            <p className="text-sm text-gray-600 mt-1">
              Guess the 5-letter word. You have {MAX_TRIES - guesses.length} {MAX_TRIES - guesses.length === 1 ? "try" : "tries"} left.
            </p>
          )}
        </div>
        {isComplete && gameState?.status === "won" && (
          <span className="text-green-600 font-semibold">✅ Solved in {gameState.tries}/6</span>
        )}
        {isComplete && gameState?.status === "lost" && (
          <span className="text-red-600 font-semibold">❌ Answer: {answer}</span>
        )}
      </div>

      {/* 6 rows */}
      <div className="flex flex-col items-center gap-2 mb-6">
      {Array.from({ length: MAX_TRIES }).map((_, row) => {
        const isCurrent = row === guesses.length && !isComplete;
        const tiles =
          row < evaluations.length
            ? evaluations[row] // submitted row
            : isCurrent
            ? currentGuess.split("").map((l) => ({ letter: l, state: "empty" as LetterState })) // typing row
            : []; // empty row

        return (
          <motion.div
            key={row}
            className={`flex gap-2 justify-center mb-1 ${isCurrent ? "ring-2 ring-purple-200 rounded-lg p-1 -m-1" : ""}`}
            animate={shakeRow && isCurrent ? { x: [-10, 10, -10, 10, 0] } : {}}
            transition={{ duration: 0.25 }}
          >
            {Array.from({ length: WORD_LENGTH }).map((_, col) => {
              const tile = tiles[col];
              const letter = tile?.letter || "";
              const state = tile?.state || "empty";
              const isSubmitted = row < evaluations.length;

              return (
                <motion.div
                  key={col}
                  className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center font-bold text-xl md:text-2xl rounded-[10px] border-2 transition-all duration-200
                    ${state === "correct" && "bg-[#10b981] text-white border-[#059669] shadow-lg shadow-[#10b981]/30"}
                    ${state === "present" && "bg-[#f59e0b] text-white border-[#d97706] shadow-lg shadow-[#f59e0b]/30"}
                    ${state === "absent" && "bg-[#475569] text-white border-[#334155] shadow-lg shadow-[#475569]/30"}
                    ${state === "empty" && isCurrent ? "bg-white border-purple-400 shadow-md" : ""}
                    ${state === "empty" && !isCurrent ? "bg-gray-100 border-gray-300" : ""}
                    ${isCurrent && col === currentGuess.length ? "ring-2 ring-purple-500 ring-opacity-75" : ""}
                  `}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={
                    isSubmitted && state !== "empty"
                      ? {
                          scale: [0.95, 1.05, 1],
                          opacity: 1,
                          rotateY: [0, 180, 360],
                        }
                      : letter && isCurrent
                      ? {
                          scale: [0.8, 1.1, 1],
                          opacity: 1,
                        }
                      : { scale: 1, opacity: 1 }
                  }
                  transition={{
                    delay: isSubmitted && state !== "empty" ? col * 0.1 : 0,
                    duration: isSubmitted && state !== "empty" ? 0.5 : letter && isCurrent ? 0.2 : 0,
                    type: isSubmitted && state !== "empty" ? "spring" : "tween",
                    stiffness: isSubmitted && state !== "empty" ? 300 : undefined,
                    damping: isSubmitted && state !== "empty" ? 20 : undefined,
                  }}
                >
                  {letter}
                </motion.div>
              );
            })}
          </motion.div>
        );
      })}
      </div>

      {/* VIRTUAL KEYBOARD */}
      <div className="mt-4 flex flex-col items-center gap-2">
        {["QWERTYUIOP", "ASDFGHJKL", "ZXCVBNM"].map((row, i) => (
          <div key={i} className="flex gap-1">
            {row.split("").map((L) => {
              const k = getKeyState(L);
              return (
                <motion.button
                  key={L}
                  onClick={() => handleKey(L)}
                  disabled={isComplete}
                  className={`
                    px-3 py-2 md:px-4 md:py-2 rounded-[6px] font-semibold min-w-[2rem] md:min-w-[2.5rem] text-sm md:text-base
                    transition-all duration-200
                    ${k === "correct" && "bg-[#10b981] text-white shadow-md shadow-[#10b981]/40"}
                    ${k === "present" && "bg-[#f59e0b] text-white shadow-md shadow-[#f59e0b]/40"}
                    ${k === "absent" && "bg-[#475569] text-white shadow-md shadow-[#475569]/40"}
                    ${k === "empty" && "bg-gray-200 text-gray-800 hover:bg-gray-300 shadow-sm hover:shadow-md"}
                  `}
                  whileHover={k === "empty" ? { scale: 1.05 } : {}}
                  whileTap={{ scale: 0.95 }}
                >
                  {L}
                </motion.button>
              );
            })}
          </div>
        ))}
        <div className="flex gap-2 mt-2">
          <motion.button
            onClick={() => handleKey("Enter")}
            disabled={isComplete || currentGuess.length !== WORD_LENGTH}
            className="bg-blue-500 text-white px-6 py-2 rounded-[6px] font-semibold hover:bg-blue-600 disabled:opacity-50 shadow-md shadow-blue-500/40 hover:shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Enter
          </motion.button>
          <motion.button
            onClick={() => handleKey("Backspace")}
            disabled={isComplete || currentGuess.length === 0}
            className="bg-gray-500 text-white px-6 py-2 rounded-[6px] font-semibold hover:bg-gray-600 disabled:opacity-50 shadow-md shadow-gray-500/40 hover:shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            ⌫
          </motion.button>
        </div>
      </div>

      {isComplete && gameState?.status === "won" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 text-center text-green-600 font-semibold"
        >
          🎉 Excellent! You got it!
        </motion.div>
      )}
    </div>
  );
}


/****************************
 Confetti Effect
****************************/
function triggerConfetti() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const canvas = document.createElement("canvas");
  canvas.style.position = "fixed";
  canvas.style.top = "0";
  canvas.style.left = "0";
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.pointerEvents = "none";
  canvas.style.zIndex = "9999";
  document.body.appendChild(canvas);

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  const particles: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    color: string;
    size: number;
  }> = [];

  const colors = ["#10b981", "#f59e0b", "#3b82f6", "#8b5cf6", "#ec4899", "#ef4444"];

  for (let i = 0; i < 50; i++) {
    particles.push({
      x: canvas.width / 2,
      y: canvas.height / 2,
      vx: (Math.random() - 0.5) * 10,
      vy: (Math.random() - 0.5) * 10 - 5,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: Math.random() * 5 + 3,
    });
  }

  let animationId: number;
  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particles.forEach((particle, index) => {
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.vy += 0.3;

      ctx.fillStyle = particle.color;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fill();

      if (particle.y > canvas.height || particle.x < 0 || particle.x > canvas.width) {
        particles.splice(index, 1);
      }
    });

    if (particles.length > 0) {
      animationId = requestAnimationFrame(animate);
    } else {
      document.body.removeChild(canvas);
    }
  };

  animate();

  setTimeout(() => {
    if (canvas.parentNode) {
      document.body.removeChild(canvas);
    }
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
  }, 3000);
}
